import { AddressDTO, AddressModel, DocumentBuyerDTO, DocumentBuyerModel, DocumentClientDTO, DocumentClientModel, DocumentExchangeRateDTO, DocumentPaymentDTO, DocumentPaymentListItemModel, DocumentVatTableItemDTO, ExchangeRateModel, InventoryDocumentBuyerDTO, InventoryDocumentClientDTO, SaleDocumentItemModel, SaleDocumentUpdateItemDTO, VatTableItemModel } from '@skylab-shared/dataModel';
import { getStartOfDate, pickBy } from '@skylab-shared/utils';

export function serializeDocumentClientModel(model: DocumentClientModel | null): DocumentClientDTO | null {
    return model ? {
        address: model.address ? serializeAddressModel(model.address) : null,
        clientId: model.sourceClientId,
        kind: model.kind,
        name: model.name,
        tinKind: model.tinKind,
        internalIdentificationNumber: model.internalIdentificationNumber,
        tin: model.tin,
        emails: model.emails,
    } : null;
}

export function serializeDocumentBuyerModel(model: DocumentBuyerModel | null): DocumentBuyerDTO | null {
    if (model) {
        const obj = serializeDocumentClientModel(model);
        if (!obj) return null;

        return {
            ...obj,
            role: model.role
        }
    }
    return null;
}

export function deserializeDocumentClientModel(model: DocumentClientDTO | null): DocumentClientModel | null {
    return model ? new DocumentClientModel(pickBy<Partial<DocumentClientModel>>({
        kind: model.kind,
        sourceClientId: model.clientId,
        name: model.name,
        emails: model.emails,
        tinKind: model.tinKind,
        internalIdentificationNumber: model.internalIdentificationNumber,
        tin: model.tin,
        address: model.address ? new AddressModel(pickBy<AddressModel>({
            name: model.address.name,
            zipCode: model.address.zipCode,
            line1: model.address.address,
            line2: model.address.line2,
            city: model.address.city,
            countryId: model.address.countryId
        }, val => val !== void 0)) : null,
    }, val => val !== void 0)) : null;
}

export function deserializeDocumentBuyerModel(model: DocumentBuyerDTO | null | undefined): DocumentBuyerModel | null {
    return model ? new DocumentBuyerModel(pickBy<Partial<DocumentBuyerModel>>({
        kind: model.kind,
        sourceClientId: model.clientId,
        name: model.name,
        emails: model.emails,
        tinKind: model.tinKind,
        internalIdentificationNumber: model.internalIdentificationNumber,
        tin: model.tin,
        role: model.role,
        address: model.address ? new AddressModel(pickBy<AddressModel>({
            name: model.address.name,
            zipCode: model.address.zipCode,
            line1: model.address.address,
            city: model.address.city,
            countryId: model.address.countryId
        }, val => val !== void 0)) : null,
    }, val => val !== void 0)) : null;
}

export function serializeInventoryDocumentClientModel(model: DocumentClientModel | null): InventoryDocumentClientDTO | null {
    return model ? {
        address: model.address ? serializeAddressModel(model.address) : null,
        contractorId: model.sourceClientId,
        kind: model.kind,
        name: model.name,
        tinKind: model.tinKind,
        internalIdentificationNumber: model.internalIdentificationNumber,
        tin: model.tin,
        emails: model.emails
    } : null;
}

export function serializeInventoryDocumentBuyerModel(model: DocumentBuyerModel | null): InventoryDocumentBuyerDTO | null {
    if (model) {
        const obj = serializeInventoryDocumentClientModel(model);
        if (!obj) return null;
        return {
            ...obj,
            role: model.role
        }
    }
    return null;
}

export function deserializeInventoryDocumentClientModel(model: InventoryDocumentClientDTO | null): DocumentClientModel | null {
    return model ? new DocumentClientModel(pickBy<Partial<DocumentClientModel>>({
        kind: model.kind,
        sourceClientId: model.contractorId,
        name: model.name,
        emails: model.emails,
        tinKind: model.tinKind,
        internalIdentificationNumber: model.internalIdentificationNumber,
        tin: model.tin,
        address: model.address ? new AddressModel(pickBy<AddressModel>({
            name: model.address.name,
            zipCode: model.address.zipCode,
            line1: model.address.address,
            city: model.address.city,
            countryId: model.address.countryId
        }, val => val !== void 0)) : null,
    }, val => val !== void 0)) : null;
}

export function deserializeInventoryDocumentBuyerModel(model: InventoryDocumentBuyerDTO | null): DocumentBuyerModel | null{
    return model ? new DocumentBuyerModel(pickBy<Partial<DocumentBuyerModel>>({
        kind: model.kind,
        sourceClientId: model.contractorId,
        name: model.name,
        emails: model.emails,
        tinKind: model.tinKind,
        internalIdentificationNumber: model.internalIdentificationNumber,
        tin: model.tin,
        role: model.role,
        address: model.address ? new AddressModel(pickBy<AddressModel>({
            name: model.address.name,
            zipCode: model.address.zipCode,
            line1: model.address.address,
            city: model.address.city,
            countryId: model.address.countryId
        }, val => val !== void 0)) : null,
    }, val => val !== void 0)) : null;
}

export function documentDeliveryAddressFilled(model: AddressModel | null): boolean {
    return model != null && Boolean(model.name || model.city || model.line1 || model.zipCode);
}

export function serializeAddressModel(model: AddressModel): AddressDTO {
    return {
        address: model.line1,
        line2: model.line2,
        city: model.city,
        countryId: model.countryId,
        name: model.name,
        zipCode: model.zipCode
    };
}

export function serializeDocumentExchangeRateModel(model: ExchangeRateModel): DocumentExchangeRateDTO {
    return {
        date: getStartOfDate(model.date),
        manual: model.manual,
        rate: model.rate,
        tableNo: model.tableNo,
        units: model.units
    };
}

export function serializeDocumentItemModel(model: SaleDocumentItemModel): SaleDocumentUpdateItemDTO {
    return {
        discountValue: model.discountValue,
        discountKind: model.discountKind,
        jpkVatGroups: model.jpkVatGroups,
        jpkVatProcedure: model.jpkVatProcedure,
        relatedInventoryItemsIds: model.relatedInventoryItemsIds || [],
        includesInventoryControl: model.includesInventoryControl,
        no: model.ordinalNumber,
        note: model.note,
        productId: model.productId,
        productName: model.productName,
        pkwiu: model.pkwiu,
        cnCode: model.cnCode,
        barCode: model.barCode,
        symbol: model.symbol,
        quantity: model.quantity,
        unitCost: model.unitCost,
        unitGrossPrice: model.price_grossBeforeDiscount,
        unitId: model.unitId,
        unitNetPrice: model.price_netBeforeDiscount,
        vatRateId: model.vatRateId
    };
}

export function serializeVatTableItemModel(model: VatTableItemModel): DocumentVatTableItemDTO {
    return {
        cost: model.cost,
        gross: model.grossValue,
        net: model.netValue,
        vat: model.vatValue,
        vatRateId: model.vatRateId
    };
}

export function serializeDocumentPaymentListItemModel(model: DocumentPaymentListItemModel): DocumentPaymentDTO {
    return {
        id: model.id,
        date: getStartOfDate(model.date),
        amount: model.value,
        onDocument: model.onDocument
    };
}
